import React, { useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  Alert,
  Button,
  CircularProgress,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import type { CategoryCounts } from '../../../../domain/reservationSlots';
import { TREATMENT_CATEGORIES } from '../../../../domain/reservationSlots';
import type { UseReservationSlotSummaryReturn } from '../hooks/useReservationSlotSummary';
import { LoadingOverlay } from '../ReservationSlotManagement.styles';
import CategoryChip from './CategoryChip';

/**
 * SummaryPanel のProps型
 *
 * @property {UseReservationSlotSummaryReturn} summary - 解放枠一覧フックの戻り値
 * @property {function} [onOpenSetup] - 設定ボタン押下時のコールバック（年・月を渡す）。未指定時は設定列を表示しない
 */
export interface SummaryPanelProps {
  summary: UseReservationSlotSummaryReturn;
  onOpenSetup?: (year: number, month: number) => void;
}

/**
 * 総枠数バッジの配色
 *
 * @description
 * モックの `.badge.b-gf`（淡い緑背景＋濃い緑文字）に対応する。
 */
const TOTAL_SLOTS_BADGE_COLORS = { bg: '#E6F7EF', fg: '#1C9A5B' } as const;

/**
 * カテゴリ別枠数をチップ列で表示する
 *
 * @param {CategoryCounts} counts - 診療カテゴリ別の枠数
 * @returns {React.ReactElement} カテゴリチップ列
 */
const CategoryCountsCell: React.FC<{ counts: CategoryCounts }> = ({ counts }) => {
  const entries = TREATMENT_CATEGORIES.filter((c) => (counts[c] ?? 0) > 0);
  if (entries.length === 0) {
    return <Typography variant="body2" color="text.secondary">—</Typography>;
  }
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {entries.map((category) => (
        <CategoryChip key={category} category={category} count={counts[category]} />
      ))}
    </Box>
  );
};

/**
 * 解放枠一覧パネル
 *
 * @description
 * 月別に生成された解放枠のサマリ（総数・カテゴリ別枠数）を一覧表示する。
 * 総枠数は緑バッジ、カテゴリ別枠数はカテゴリ色チップで表示し、
 * 一覧はクライアントサイドでページングする。
 * `onOpenSetup` が渡された場合のみ、月ごとの設定画面へ遷移する「設定」列を表示する。
 *
 * @param {SummaryPanelProps} props - コンポーネントProps
 * @returns {React.ReactElement} 解放枠一覧パネル
 */
const SummaryPanel: React.FC<SummaryPanelProps> = ({ summary, onOpenSetup }) => {
  const {
    summaries,
    pagedSummaries,
    page,
    rowsPerPage,
    totalCount,
    setPage,
    isLoading,
    error,
    reload,
    clearError,
  } = summary;

  /**
   * ページ変更ハンドラ
   *
   * @param {unknown} _event - クリックイベント（未使用）
   * @param {number} newPage - 変更後のページ番号（0始まり）
   */
  const handleChangePage = useCallback(
    (_event: unknown, newPage: number) => {
      setPage(newPage);
    },
    [setPage]
  );

  return (
    <Box sx={{ position: 'relative' }}>
      {isLoading && (
        <LoadingOverlay>
          <CircularProgress />
        </LoadingOverlay>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={reload} disabled={isLoading}>
          更新
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      {summaries.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          解放枠がまだ生成されていません。
        </Typography>
      ) : (
        <Paper variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>対象月</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'center', minWidth: 100 }}>総枠数</TableCell>
                  <TableCell sx={{ fontWeight: 600, minWidth: 240 }}>カテゴリ別枠数</TableCell>
                  {onOpenSetup && (
                    <TableCell sx={{ fontWeight: 600, textAlign: 'center', minWidth: 80 }}>設定</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedSummaries.map((item) => (
                  <TableRow key={`${item.year}-${item.month}`} hover>
                    <TableCell sx={{ fontWeight: 500 }}>
                      {item.year}年{String(item.month).padStart(2, '0')}月
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Chip
                        label={`${item.totalSlots}枠`}
                        size="small"
                        sx={{
                          backgroundColor: TOTAL_SLOTS_BADGE_COLORS.bg,
                          color: TOTAL_SLOTS_BADGE_COLORS.fg,
                          fontWeight: 700,
                          borderRadius: 1,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <CategoryCountsCell counts={item.categoryCounts} />
                    </TableCell>
                    {onOpenSetup && (
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => onOpenSetup(item.year, item.month)}
                        >
                          設定
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* ページネーション（モックの「1-2 / 2件 ‹ ›」に対応） */}
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            rowsPerPageOptions={[]}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}件`}
          />
        </Paper>
      )}
    </Box>
  );
};

export default SummaryPanel;
